import React, { createContext, useState, useEffect, useCallback } from "react";

export const VideosContext = createContext();

export function VideosProvider({ children }) {
  const [videos, setVideos] = useState([]);

  const shuffle = useCallback((arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }, []);

  const loadVideos = useCallback(() => {
    // Read selected language from localStorage, default to "English"
    const selectedLanguage = localStorage.getItem("userLanguage") || "English";
    const storageKey = `videosList_${selectedLanguage}`;

    // Check if videos for this language are already in sessionStorage
    if (sessionStorage.getItem("videosLoaded") && sessionStorage.getItem(storageKey)) {
      const saved = sessionStorage.getItem(storageKey);
      setVideos(JSON.parse(saved));
      console.log(`Loaded videos from sessionStorage for ${selectedLanguage}`);
      return;
    }

    // Fetch and filter videos based on language
    fetch("/videos.json")
      .then((r) => r.json())
      .then((data) => {
        // Select the container based on language (lowercase for consistency)
        let selectedVideos = data[selectedLanguage.toLowerCase()] || data.english || [];
        
        // Safeguard: Flatten array if nested (handles malformed JSON)
        if (Array.isArray(selectedVideos) && selectedVideos.length === 1 && Array.isArray(selectedVideos[0])) {
          selectedVideos = selectedVideos[0];
          console.warn(`Flattened nested array for ${selectedLanguage} container`);
        }

        const shuffled = shuffle(selectedVideos);
        setVideos(shuffled);
        sessionStorage.setItem("videosLoaded", "true");
        sessionStorage.setItem(storageKey, JSON.stringify(shuffled));
        console.log(`Fetched and shuffled videos for ${selectedLanguage}`);
      })
      .catch((error) => {
        console.error("Error loading videos:", error);
        setVideos([]);
      });
  }, [shuffle]);

  useEffect(() => {
    // Initial load
    loadVideos();
  }, [loadVideos]);

  // Listen for language changes (both same-tab and cross-tab)
  useEffect(() => {
    // Handle cross-tab storage events
    const handleStorageChange = (event) => {
      if (event.key === "userLanguage") {
        console.log("Language changed to:", event.newValue);
        // Clear previous sessionStorage for videos
        sessionStorage.removeItem("videosLoaded");
        sessionStorage.removeItem(`videosList_English`);
        sessionStorage.removeItem(`videosList_Hindi`);
        sessionStorage.removeItem(`videosList_Both`);
        loadVideos();
      }
    };

    // Custom event for same-tab localStorage changes
    const handleLocalStorageChange = () => {
      // Dispatch a custom event to simulate storage event in the same tab
      const event = new Event("storage");
      event.key = "userLanguage";
      event.newValue = localStorage.getItem("userLanguage");
      window.dispatchEvent(event);
    };

    // Override localStorage.setItem to dispatch custom event
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, [key, value]);
      if (key === "userLanguage") {
        console.log("localStorage.setItem called for userLanguage:", value);
        handleLocalStorageChange();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      // Restore original setItem and remove event listener
      localStorage.setItem = originalSetItem;
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [loadVideos]);

  return (
    <VideosContext.Provider value={{ videos, setVideos }}>
      {children}
    </VideosContext.Provider>
  );
}